import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap'
import { Ansible } from 'interfaces/ansible'
import useUpdateAnsible from 'hooks/use-update-ansible'

interface AnsibleCapacityEditModalProps {
  show: boolean
  onHide: () => void
  ansible: Ansible
  onSuccess?: () => void
}

const AnsibleCapacityEditModal: React.FC<AnsibleCapacityEditModalProps> = ({
  show,
  onHide,
  ansible,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    maxMacMinis: ansible.maxMacMinis,
    maxPhysicalPhones: ansible.maxPhysicalPhones,
    currentMacMinis: ansible.currentMacMinis,
    currentPhysicalPhones: ansible.currentPhysicalPhones,
  })

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  const { mutate: updateAnsible, isLoading } = useUpdateAnsible()

  // Reset form when ansible changes
  useEffect(() => {
    if (ansible) {
      setFormData({
        maxMacMinis: ansible.maxMacMinis,
        maxPhysicalPhones: ansible.maxPhysicalPhones,
        currentMacMinis: ansible.currentMacMinis,
        currentPhysicalPhones: ansible.currentPhysicalPhones,
      })
      setValidationErrors([])
      setShowConfirmation(false)
    }
  }, [ansible, show])

  const handleInputChange = (field: keyof typeof formData, value: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    setValidationErrors([]) // Clear errors when user makes changes
  }

  const validateForm = (): string[] => {
    const errors: string[] = []

    // Validate that current counts don't exceed max capacity
    if (formData.currentMacMinis > formData.maxMacMinis) {
      errors.push(
        `Current Mac Minis (${formData.currentMacMinis}) cannot exceed max capacity (${formData.maxMacMinis})`
      )
    }

    if (formData.currentPhysicalPhones > formData.maxPhysicalPhones) {
      errors.push(
        `Current Physical Phones (${formData.currentPhysicalPhones}) cannot exceed max capacity (${formData.maxPhysicalPhones})`
      )
    }

    // Validate minimum values
    if (formData.maxMacMinis < 0 || formData.maxPhysicalPhones < 0) {
      errors.push('Maximum capacity values cannot be negative')
    }

    if (formData.currentMacMinis < 0 || formData.currentPhysicalPhones < 0) {
      errors.push('Current capacity values cannot be negative')
    }

    return errors
  }

  const hasChanges = () => {
    return (
      formData.maxMacMinis !== ansible.maxMacMinis ||
      formData.maxPhysicalPhones !== ansible.maxPhysicalPhones ||
      formData.currentMacMinis !== ansible.currentMacMinis ||
      formData.currentPhysicalPhones !== ansible.currentPhysicalPhones
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const errors = validateForm()
    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    if (!hasChanges()) {
      onHide()
      return
    }

    setShowConfirmation(true)
  }

  const handleConfirmUpdate = () => {
    updateAnsible(
      {
        ansibleId: ansible.id,
        ...formData,
      },
      {
        onSuccess: () => {
          onSuccess?.()
          onHide()
        },
      }
    )
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    setValidationErrors([])
  }

  const getChangeSummary = () => {
    const changes: string[] = []

    if (formData.maxMacMinis !== ansible.maxMacMinis) {
      changes.push(
        `Max Mac Minis: ${ansible.maxMacMinis} → ${formData.maxMacMinis}`
      )
    }

    if (formData.maxPhysicalPhones !== ansible.maxPhysicalPhones) {
      changes.push(
        `Max Physical Phones: ${ansible.maxPhysicalPhones} → ${formData.maxPhysicalPhones}`
      )
    }

    if (formData.currentMacMinis !== ansible.currentMacMinis) {
      changes.push(
        `Current Mac Minis: ${ansible.currentMacMinis} → ${formData.currentMacMinis}`
      )
    }

    if (formData.currentPhysicalPhones !== ansible.currentPhysicalPhones) {
      changes.push(
        `Current Physical Phones: ${ansible.currentPhysicalPhones} → ${formData.currentPhysicalPhones}`
      )
    }

    return changes
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {showConfirmation
            ? 'Confirm Capacity Changes'
            : `Edit Capacity - ${ansible.name}`}
        </Modal.Title>
      </Modal.Header>

      {showConfirmation ? (
        // Confirmation View
        <>
          <Modal.Body>
            <Alert variant="warning">
              <Alert.Heading>⚠️ Confirm Capacity Changes</Alert.Heading>
              <p>
                You are about to modify the capacity settings for ansible{' '}
                <strong>{ansible.name}</strong>. This could affect device
                assignments and system operations.
              </p>
              <hr />
              <h6>Changes to be made:</h6>
              <ul className="mb-0">
                {getChangeSummary().map((change, index) => (
                  <li key={index}>{change}</li>
                ))}
              </ul>
            </Alert>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handleConfirmUpdate}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                'Confirm Changes'
              )}
            </Button>
          </Modal.Footer>
        </>
      ) : (
        // Edit Form View
        <>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {validationErrors.length > 0 && (
                <Alert variant="danger">
                  <h6>Validation Errors:</h6>
                  <ul className="mb-0">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Row>
                <Col md={6}>
                  <h6 className="mb-3">Mac Mini Capacity</h6>

                  <Form.Group className="mb-3">
                    <Form.Label>Maximum Mac Minis</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      value={formData.maxMacMinis}
                      onChange={(e) =>
                        handleInputChange(
                          'maxMacMinis',
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                    <Form.Text className="text-muted">
                      Maximum number of Mac Minis this ansible can hold
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Current Mac Minis</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max={formData.maxMacMinis}
                      value={formData.currentMacMinis}
                      onChange={(e) =>
                        handleInputChange(
                          'currentMacMinis',
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                    <Form.Text className="text-muted">
                      Number of Mac Minis currently assigned to this ansible
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <h6 className="mb-3">Physical Phone Capacity</h6>

                  <Form.Group className="mb-3">
                    <Form.Label>Maximum Physical Phones</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="300"
                      value={formData.maxPhysicalPhones}
                      onChange={(e) =>
                        handleInputChange(
                          'maxPhysicalPhones',
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                    <Form.Text className="text-muted">
                      Maximum number of physical phones this ansible can hold
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Current Physical Phones</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max={formData.maxPhysicalPhones}
                      value={formData.currentPhysicalPhones}
                      onChange={(e) =>
                        handleInputChange(
                          'currentPhysicalPhones',
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                    <Form.Text className="text-muted">
                      Number of physical phones currently assigned to this
                      ansible
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Alert variant="info" className="mt-3">
                <strong>Note:</strong> Changing capacity values may affect
                device assignments. Make sure these numbers accurately reflect
                your current setup.
              </Alert>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={onHide}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={!hasChanges()}>
                Review Changes
              </Button>
            </Modal.Footer>
          </Form>
        </>
      )}
    </Modal>
  )
}

export default AnsibleCapacityEditModal
